﻿import { Card, CardBody } from "@progress/kendo-react-layout";
import { CreditCard, DollarSign, Megaphone, Target, TrendingUp } from "lucide-react";
import { useMemo } from "react";
import type { CommercialPlanData } from "../../../../models/commericalPlanModels";
import { calculateBaselineRevenue, calculateCommercialPlanMetrics, calculateRetailMediaSpend, calculateTradeSpend } from "../../../../utils/commercialPlanCalculations";
import { formatCurrency, formatPercentage, isDefinedNumber } from "../../../../utils/numberValueConversion";
import { SovraSkeleton } from "../../../../components/skeleton/sovraSkeleton";

type SummaryMetricFormat = "currency" | "percent" | "number";
type SummaryMetric = {
    title: string;
    value: number | null;
    format: SummaryMetricFormat;
    icon: React.ReactNode;
    delta?: string;
};

type SimulatorSummaryProps = {
    planData?: CommercialPlanData | null;
};

const renderSummarySkeletonCards = (): React.ReactNode => (
    <SummaryMetricsGrid>
        {Array.from({ length: 5 }, (_, index) => (
            <div key={index} className="d-flex min-w-0">
                <SummaryMetricCardShell>
                    <div className="d-flex align-items-start gap-2">
                        <SovraSkeleton
                            shape="rectangle"
                            style={{ width: 42, height: 42, borderRadius: 14 }}
                        />

                        {index < 2 && (
                            <SovraSkeleton
                                shape="rectangle"
                                style={{ width: 58, height: 24, borderRadius: 999 }}
                            />
                        )}
                    </div>

                    <div>
                        <div className="mb-2">
                            <SovraSkeleton
                                shape="text"
                                style={{ width: 124, height: 28 }}
                            />
                        </div>

                        <SovraSkeleton
                            shape="text"
                            style={{ width: 150, height: 18 }}
                        />
                    </div>
                </SummaryMetricCardShell>
            </div>
        ))}
    </SummaryMetricsGrid>
);

const getSummaryMetrics = (planData?: CommercialPlanData): SummaryMetric[] => {
    if (!planData) {
        return [];
    }

    const { projectedRevenue, projectedProfit, roi } = calculateCommercialPlanMetrics(planData);
    const baselineRevenue = calculateBaselineRevenue(planData);
    const incrementalRevenue = isDefinedNumber(projectedRevenue) && isDefinedNumber(baselineRevenue)
        ? projectedRevenue - baselineRevenue
        : null;
    const tradeSpend = calculateTradeSpend(planData);
    const retailMediaSpend = calculateRetailMediaSpend(planData);

    return [
        {
            title: "Projected Revenue",
            value: projectedRevenue,
            format: "currency",
            icon: <DollarSign style={{ width: "14px", height: "14px" }} />,
            delta: "+12.4%"
        },
        {
            title: "Incremental Revenue",
            value: incrementalRevenue,
            format: "currency",
            icon: <TrendingUp style={{ width: "14px", height: "14px" }} />,
            delta: "+10.4%"
        },
        {
            title: "Projected Profit",
            value: projectedProfit,
            format: "currency",
            icon: <TrendingUp style={{ width: "14px", height: "14px" }} />,
            delta: "+8.2%"
        },
        {
            title: "ROI",
            value: roi,
            format: "percent",
            icon: <Target style={{ width: "14px", height: "14px" }} />
        },
        {
            title: "Trade Spend",
            value: tradeSpend,
            format: "currency",
            icon: <CreditCard style={{ width: "14px", height: "14px" }} />
        },
        {
            title: "Retail Media Spend",
            value: retailMediaSpend,
            format: "currency",
            icon: <Megaphone style={{ width: "14px", height: "14px" }} />,
            delta: "+12.4%"
        }
    ];
};

const formatMetricValue = (metric: SummaryMetric): string => {
    switch (metric.format) {
        case "currency":
            return formatCurrency(metric.value);
        case "percent":
            return formatPercentage(metric.value);
        case "number":
            return metric.value?.toLocaleString() ?? "-";
    }
};

const SummaryMetricCardShell = ({ children }: { children: React.ReactNode }) => {
    return (
        <Card className="simulator-summary-card border rounded-3 h-100 w-100 shadow-sm shadow-md-hover">
            <CardBody className="d-flex flex-column gap-2">{children}</CardBody>
        </Card>
    );
};

const SummaryMetricsGrid = ({ children }: { children: React.ReactNode }) => {
    return (
        <div className="simulator-plan-grid simulator-plan-grid--metrics min-w-0">
            {children}
        </div>
    );
};

const SimulatorSummary: React.FC<SimulatorSummaryProps> = ({ planData }) => {
    const metrics = useMemo<SummaryMetric[]>(
        () => planData ? getSummaryMetrics(planData) : [],
        [planData]
    );

    if (!planData) {
        return renderSummarySkeletonCards();
    }

    return (
        <SummaryMetricsGrid>
            {metrics.map((metric, index) => (
                <div
                    key={`summary_${index}`}
                    className="d-flex min-w-0"
                >
                    <SummaryMetricCardShell>
                        <div className="simulator-summary-metrics d-flex gap-2 min-w-0">
                            <span className="simulator-summary-icon d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0">
                                {metric.icon}
                            </span>

                            <div className="simulator-summary-value fw-bold text-body lh-sm mb-0 min-w-0 flex-grow-1">
                                {formatMetricValue(metric)}
                            </div>

                            {isDefinedNumber(metric.value) && metric.delta && (
                                <span className="badge rounded-pill fw-semibold simulator-summary-delta flex-shrink-0">
                                    {metric.delta}
                                </span>
                            )}
                        </div>

                        <p className="simulator-summary-title text-secondary mb-0">
                            {metric.title}
                        </p>
                    </SummaryMetricCardShell>
                </div>
            ))}
        </SummaryMetricsGrid>
    );
};

export default SimulatorSummary;
